import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Save,
  Trash2,
  FlaskConical,
  Factory,
  Warehouse,
  DollarSign,
  ScanSearch,
  ShoppingCart,
  AlertTriangle,
} from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "../api";
import { Skeleton } from "../components/Skeleton";
import type { Product } from "../types";

export function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: product, isLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: () => api.getProduct(Number(id)),
    enabled: !!id,
  });

  const [form, setForm] = useState<Partial<Product>>({});

  useEffect(() => {
    if (product) setForm(product);
  }, [product]);

  const updateMutation = useMutation({
    mutationFn: (d: Partial<Product>) => api.updateProduct(Number(id), d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product", id] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteProduct(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      navigate("/products");
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-slate-400 text-sm">載入中...</div>;
  }

  if (!product) {
    return <div className="text-center py-12 text-slate-400 text-sm">找不到產品</div>;
  }

  return (
    <div className="max-w-3xl space-y-6 animate-fade-in">
      <button
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
        onClick={() => navigate("/products")}
      >
        <ArrowLeft size={16} /> 返回產品清單
      </button>

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">{product.name}</h1>
        <button
          className="btn btn-danger"
          onClick={() => {
            if (confirm("確定要停用此產品？")) deleteMutation.mutate();
          }}
        >
          <Trash2 size={14} /> 停用
        </button>
      </div>

      <form
        className="rounded-xl border border-slate-200 bg-white p-6 space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          const { id: _id, created_at: _c, updated_at: _u, ...rest } = form;
          updateMutation.mutate(rest);
        }}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">SKU</label>
            <input
              className="input font-mono"
              value={form.sku ?? ""}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">產品名稱</label>
            <input
              className="input"
              value={form.name ?? ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">類別</label>
            <input
              className="input"
              value={form.category ?? ""}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">狀態</label>
            <select
              className="input"
              value={form.status ?? "active"}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="rd">研發中</option>
              <option value="active">上架</option>
              <option value="discontinued">停用</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">售價</label>
            <input
              className="input font-mono"
              type="number"
              step="0.01"
              value={form.unit_price ?? ""}
              onChange={(e) =>
                setForm({ ...form, unit_price: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">成本</label>
            <input
              className="input font-mono"
              type="number"
              step="0.01"
              value={form.unit_cost ?? ""}
              onChange={(e) =>
                setForm({ ...form, unit_cost: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </div>
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-1">描述</label>
          <textarea
            className="input min-h-24"
            value={form.description ?? ""}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={updateMutation.isPending}
        >
          <Save size={14} /> {updateMutation.isPending ? "儲存中..." : "儲存"}
        </button>
        {updateMutation.isSuccess && (
          <span className="text-xs text-emerald-600 ml-3">已更新</span>
        )}
      </form>

      <div className="text-xs text-slate-400 space-y-1">
        <p>建立時間：{new Date(product.created_at).toLocaleString("zh-TW")}</p>
        <p>更新時間：{new Date(product.updated_at).toLocaleString("zh-TW")}</p>
      </div>

      {/* ================================================================= */}
      {/* Cross-module data hub tabs                                        */}
      {/* ================================================================= */}
      <CrossModuleTabs product={product} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

const HUB_TABS = [
  { key: "bom", label: "配方", icon: FlaskConical },
  { key: "production", label: "生產工單", icon: Factory },
  { key: "inventory", label: "庫存", icon: Warehouse },
  { key: "pricing", label: "報價", icon: DollarSign },
  { key: "traceability", label: "追溯", icon: ScanSearch },
  { key: "sales", label: "客戶訂單", icon: ShoppingCart },
] as const;

type TabKey = (typeof HUB_TABS)[number]["key"];

// ---------------------------------------------------------------------------
// Helper: fetch JSON with credentials
// ---------------------------------------------------------------------------

const fetchJson = (url: string) =>
  fetch(url, { credentials: "include" }).then((r) => {
    if (!r.ok) throw new Error(`${r.status}`);
    return r.json();
  });

function toArray(data: unknown): any[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && "items" in data && Array.isArray((data as any).items)) return (data as any).items;
  return [];
}

// ---------------------------------------------------------------------------
// Empty / Error states
// ---------------------------------------------------------------------------

function EmptyState({ message }: { message: string }) {
  return (
    <div className="py-12 text-center text-sm text-slate-400">{message}</div>
  );
}

function ErrorState({ message }: { message?: string }) {
  return (
    <div className="py-10 text-center text-sm text-red-400 flex items-center justify-center gap-2">
      <AlertTriangle size={16} /> {message ?? "載入失敗，請稍後再試"}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab badge
// ---------------------------------------------------------------------------

function Badge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-slate-100 text-[10px] font-semibold text-slate-500">
      {count}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Shared table wrapper
// ---------------------------------------------------------------------------

function DataTable({
  headers,
  children,
}: {
  headers: string[];
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/60">
            {headers.map((h) => (
              <th
                key={h}
                className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">{children}</tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CrossModuleTabs component
// ---------------------------------------------------------------------------

function CrossModuleTabs({ product }: { product: Product }) {
  const [activeTab, setActiveTab] = useState<TabKey>("bom");

  // ---- Data queries (all enabled, react-query deduplicates) ----

  const bomProducts = useQuery({
    queryKey: ["bom-products"],
    queryFn: () => fetchJson("/api/bom/products"),
  });

  const prodOrders = useQuery({
    queryKey: ["prod-orders"],
    queryFn: () => fetchJson("/api/production/orders"),
  });

  const invStock = useQuery({
    queryKey: ["inv-stock"],
    queryFn: () => fetchJson("/api/inventory/stock"),
  });

  const pricingSims = useQuery({
    queryKey: ["pricing-sims"],
    queryFn: () => fetchJson("/api/pricing/simulations"),
  });

  const bomBatches = useQuery({
    queryKey: ["bom-batches"],
    queryFn: () => fetchJson("/api/bom/batches"),
  });

  const salesOrders = useQuery({
    queryKey: ["crm-orders"],
    queryFn: () => fetchJson("/api/crm/orders"),
  });

  // ---- Derived filtered data ----

  const bomMatch = toArray(bomProducts.data).find(
    (p: any) => p.sku === product.sku,
  );
  const bomItems: any[] = bomMatch?.bom_items ?? bomMatch?.ingredients ?? [];

  const filteredOrders: any[] = toArray(prodOrders.data).filter(
    (o: any) =>
      o.product_sku === product.sku || o.product_name === product.name,
  );

  const filteredStock: any[] = toArray(invStock.data).filter(
    (s: any) => s.product_sku === product.sku,
  );

  const filteredPricing: any[] = toArray(pricingSims.data).filter(
    (p: any) =>
      p.product_name === product.name || p.product_sku === product.sku,
  );

  const filteredBatches: any[] = toArray(bomBatches.data).filter(
    (b: any) => b.product_sku === product.sku,
  );

  const filteredSales: any[] = toArray(salesOrders.data).filter(
    (o: any) =>
      o.product_name === product.name || o.product_sku === product.sku,
  );

  // Badge counts keyed by tab
  const counts: Record<TabKey, number> = {
    bom: bomItems.length,
    production: filteredOrders.length,
    inventory: filteredStock.length,
    pricing: filteredPricing.length,
    traceability: filteredBatches.length,
    sales: filteredSales.length,
  };

  // Loading states keyed by tab
  const loading: Record<TabKey, boolean> = {
    bom: bomProducts.isLoading,
    production: prodOrders.isLoading,
    inventory: invStock.isLoading,
    pricing: pricingSims.isLoading,
    traceability: bomBatches.isLoading,
    sales: salesOrders.isLoading,
  };

  const errored: Record<TabKey, boolean> = {
    bom: bomProducts.isError,
    production: prodOrders.isError,
    inventory: invStock.isError,
    pricing: pricingSims.isError,
    traceability: bomBatches.isError,
    sales: salesOrders.isError,
  };

  return (
    <section className="space-y-0">
      {/* Tab bar */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-1 overflow-x-auto">
          {HUB_TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 pb-2.5 pt-1 text-sm font-medium whitespace-nowrap transition-colors relative ${
                  active
                    ? "text-qimei-500 border-b-2 border-qimei-500"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                <Icon size={14} />
                {tab.label}
                {!loading[tab.key] && <Badge count={counts[tab.key]} />}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div className="pt-4 animate-fade-in">
        {/* BOM */}
        {activeTab === "bom" && (
          <TabPanel loading={loading.bom} error={errored.bom}>
            {bomItems.length === 0 ? (
              <EmptyState message="尚無配方資料" />
            ) : (
              <DataTable headers={["原料名稱", "用量", "單位"]}>
                {bomItems.map((item: any, i: number) => (
                  <tr key={i} className="hover:bg-slate-50/50">
                    <td className="px-4 py-2.5 text-slate-700">
                      {item.ingredient_name ?? item.name ?? "-"}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-600">
                      {item.quantity ?? item.amount ?? "-"}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">
                      {item.unit ?? "-"}
                    </td>
                  </tr>
                ))}
              </DataTable>
            )}
          </TabPanel>
        )}

        {/* Production orders */}
        {activeTab === "production" && (
          <TabPanel loading={loading.production} error={errored.production}>
            {filteredOrders.length === 0 ? (
              <EmptyState message="尚無生產工單" />
            ) : (
              <DataTable
                headers={[
                  "工單編號",
                  "數量",
                  "狀態",
                  "排程日期",
                  "產線",
                ]}
              >
                {filteredOrders.map((o: any) => (
                  <tr key={o.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-2.5">
                      <Link
                        to="/production"
                        className="text-qimei-500 hover:underline font-mono"
                      >
                        #{o.id}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-600">
                      {o.quantity ?? "-"}
                    </td>
                    <td className="px-4 py-2.5">
                      <OrderStatusBadge status={o.status} />
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">
                      {o.scheduled_start
                        ? new Date(o.scheduled_start).toLocaleDateString("zh-TW")
                        : "-"}
                      {o.scheduled_end
                        ? ` ~ ${new Date(o.scheduled_end).toLocaleDateString("zh-TW")}`
                        : ""}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">
                      {o.line_name ?? o.production_line ?? "-"}
                    </td>
                  </tr>
                ))}
              </DataTable>
            )}
          </TabPanel>
        )}

        {/* Inventory */}
        {activeTab === "inventory" && (
          <TabPanel loading={loading.inventory} error={errored.inventory}>
            {filteredStock.length === 0 ? (
              <EmptyState message="尚無庫存資料" />
            ) : (
              <DataTable
                headers={["倉庫", "庫存量", "單位", "安全庫存", "狀態"]}
              >
                {filteredStock.map((s: any) => (
                  <tr key={s.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-2.5">
                      <Link
                        to="/inventory"
                        className="text-qimei-500 hover:underline"
                      >
                        {s.warehouse ?? "-"}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-600">
                      {s.quantity ?? "-"}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">
                      {s.unit ?? "-"}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-500">
                      {s.min_threshold ?? "-"}
                    </td>
                    <td className="px-4 py-2.5">
                      {s.low_stock ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
                          <AlertTriangle size={12} /> 低庫存
                        </span>
                      ) : (
                        <span className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">
                          正常
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </DataTable>
            )}
          </TabPanel>
        )}

        {/* Pricing */}
        {activeTab === "pricing" && (
          <TabPanel loading={loading.pricing} error={errored.pricing}>
            {filteredPricing.length === 0 ? (
              <EmptyState message="尚無報價模擬資料" />
            ) : (
              <DataTable
                headers={[
                  "模擬名稱",
                  "建議售價",
                  "毛利率",
                  "狀態",
                  "建立時間",
                ]}
              >
                {filteredPricing.map((p: any) => (
                  <tr key={p.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-2.5">
                      <Link
                        to="/pricing"
                        className="text-qimei-500 hover:underline"
                      >
                        {p.name ?? p.title ?? `模擬 #${p.id}`}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-600">
                      {p.suggested_price != null
                        ? `$${Number(p.suggested_price).toLocaleString()}`
                        : "-"}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-600">
                      {p.margin != null
                        ? `${(Number(p.margin) * 100).toFixed(1)}%`
                        : p.gross_margin != null
                          ? `${(Number(p.gross_margin) * 100).toFixed(1)}%`
                          : "-"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">
                      {p.status ?? "-"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-400">
                      {p.created_at
                        ? new Date(p.created_at).toLocaleDateString("zh-TW")
                        : "-"}
                    </td>
                  </tr>
                ))}
              </DataTable>
            )}
          </TabPanel>
        )}

        {/* Traceability */}
        {activeTab === "traceability" && (
          <TabPanel
            loading={loading.traceability}
            error={errored.traceability}
          >
            {filteredBatches.length === 0 ? (
              <EmptyState message="尚無批次追溯資料" />
            ) : (
              <DataTable headers={["批號", "生產日期", "數量"]}>
                {filteredBatches.map((b: any) => (
                  <tr key={b.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-2.5">
                      <Link
                        to="/traceability"
                        className="text-qimei-500 hover:underline font-mono"
                      >
                        {b.lot_number ?? b.batch_number ?? "-"}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">
                      {b.produced_at
                        ? new Date(b.produced_at).toLocaleString("zh-TW")
                        : "-"}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-600">
                      {b.quantity ?? "-"}
                    </td>
                  </tr>
                ))}
              </DataTable>
            )}
          </TabPanel>
        )}

        {/* Sales orders */}
        {activeTab === "sales" && (
          <TabPanel loading={loading.sales} error={errored.sales}>
            {filteredSales.length === 0 ? (
              <EmptyState message="尚無客戶訂單" />
            ) : (
              <DataTable
                headers={["訂單編號", "客戶", "數量", "狀態", "日期"]}
              >
                {filteredSales.map((o: any) => (
                  <tr key={o.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-2.5">
                      <Link
                        to="/crm"
                        className="text-qimei-500 hover:underline font-mono"
                      >
                        #{o.id}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">
                      {o.customer_name ?? o.customer ?? "-"}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-600">
                      {o.quantity ?? "-"}
                    </td>
                    <td className="px-4 py-2.5">
                      <OrderStatusBadge status={o.status} />
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-400">
                      {o.order_date ?? o.created_at
                        ? new Date(
                            o.order_date ?? o.created_at,
                          ).toLocaleDateString("zh-TW")
                        : "-"}
                    </td>
                  </tr>
                ))}
              </DataTable>
            )}
          </TabPanel>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// TabPanel wrapper (loading / error handling)
// ---------------------------------------------------------------------------

function TabPanel({
  loading,
  error,
  children,
}: {
  loading: boolean;
  error: boolean;
  children: React.ReactNode;
}) {
  if (loading) return <Skeleton variant="table" rows={4} />;
  if (error) return <ErrorState />;
  return <div className="animate-fade-in">{children}</div>;
}

// ---------------------------------------------------------------------------
// Order status badge (reusable)
// ---------------------------------------------------------------------------

function OrderStatusBadge({ status }: { status?: string }) {
  if (!status) return <span className="text-xs text-slate-400">-</span>;

  const map: Record<string, string> = {
    pending: "bg-amber-50 text-amber-600 border-amber-100",
    scheduled: "bg-blue-50 text-blue-600 border-blue-100",
    in_progress: "bg-violet-50 text-violet-600 border-violet-100",
    running: "bg-violet-50 text-violet-600 border-violet-100",
    completed: "bg-emerald-50 text-emerald-600 border-emerald-100",
    done: "bg-emerald-50 text-emerald-600 border-emerald-100",
    cancelled: "bg-slate-100 text-slate-500 border-slate-200",
    shipped: "bg-sky-50 text-sky-600 border-sky-100",
    delivered: "bg-emerald-50 text-emerald-600 border-emerald-100",
  };

  const cls =
    map[status.toLowerCase()] ?? "bg-slate-50 text-slate-500 border-slate-200";

  return (
    <span
      className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${cls}`}
    >
      {status}
    </span>
  );
}
